import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import {
  CodeValidationResult,
  PROTOCOL_DICTIONARY_LABEL,
  Protocol,
  ProtocolCodeField,
  ProtocolDrugTrademark,
  ProtocolTrademarksByInn,
} from '@lib/protocols/data-access';
import { TranslatePipe } from '@ngx-translate/core';

interface CodeSection {
  titleKey: string;
  items: ProtocolCodeField[];
  showCode: boolean;
  codeWidthClass: string;
}

// Fixed code-badge widths per dictionary (includes the badge padding) so labels align
// regardless of content length: ICD/ICPC ≤7, ACHI ≤12, laboratory ≤13 symbols.
const ICD_CODE_WIDTH = 'min-w-[4.5rem]';
const ACHI_CODE_WIDTH = 'min-w-[7rem]';
const LAB_CODE_WIDTH = 'min-w-[7.5rem]';

interface TextSection {
  titleKey: string;
  items: string[];
}

interface UnresolvedGroup {
  label: string;
  codes: string[];
}

interface TrademarkGroup {
  name: string;
  morionCodes: string[];
  isRecommended: boolean;
  requiresRecipe: boolean;
}

interface PharmacologicalEntry {
  inn: ProtocolCodeField;
  trademarks: TrademarkGroup[];
}

@Component({
  selector: 'protocols-code-view',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './protocol-code-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProtocolCodeViewComponent {
  readonly protocol = input.required<Protocol>();
  readonly unresolved = input<CodeValidationResult[]>([]);
  readonly trademarks = input<ProtocolTrademarksByInn>({});

  protected readonly identifiers = computed(() =>
    this.protocol()
      .identifiers.map(id => [id.icd10Code, id.icpcCode].filter(Boolean).join(' / '))
      .filter(Boolean)
  );

  protected readonly sections = computed<CodeSection[]>(() => {
    const protocol = this.protocol();
    return [
      { titleKey: 'protocols.detail.section.differentialDiagnoses', items: protocol.differentialDiagnoses, showCode: true, codeWidthClass: ICD_CODE_WIDTH },
      { titleKey: 'protocols.detail.section.instrumentalDiagnoses', items: protocol.instrumentalDiagnoses, showCode: true, codeWidthClass: ACHI_CODE_WIDTH },
      { titleKey: 'protocols.detail.section.laboratoryServices', items: protocol.laboratoryServices, showCode: true, codeWidthClass: LAB_CODE_WIDTH },
      { titleKey: 'protocols.detail.section.relatedSpecialists', items: protocol.relatedSpecialists, showCode: true, codeWidthClass: ACHI_CODE_WIDTH },
      { titleKey: 'protocols.detail.section.nonPharmacologicalTreatments', items: protocol.nonPharmacologicalTreatments, showCode: true, codeWidthClass: ACHI_CODE_WIDTH },
      { titleKey: 'protocols.detail.section.surgicalEndoscopicTreatments', items: protocol.surgicalEndoscopicTreatments, showCode: true, codeWidthClass: ACHI_CODE_WIDTH },
      { titleKey: 'protocols.detail.section.rehabilitation', items: protocol.rehabilitation, showCode: true, codeWidthClass: ACHI_CODE_WIDTH },
    ].filter(section => section.items.length > 0);
  });

  protected readonly pharmacological = computed<PharmacologicalEntry[]>(() => {
    // The trademark map is keyed by INN identifier case-insensitively on the backend,
    // so normalize keys before matching against each treatment's code.
    const byInn = new Map(Object.entries(this.trademarks()).map(([key, value]) => [key.toLowerCase(), value]));
    return this.protocol().pharmacologicalTreatments.map(inn => ({
      inn,
      trademarks: this.groupTrademarks(byInn.get(inn.code.toLowerCase()) ?? []),
    }));
  });

  // Collapse trademarks that share a name (case-insensitive) into a single row,
  // listing every matched Morion code and OR-ing the flags across the group.
  private groupTrademarks(trademarks: ProtocolDrugTrademark[]): TrademarkGroup[] {
    const groups = new Map<string, TrademarkGroup>();
    for (const trademark of trademarks) {
      const key = trademark.name.trim().toLowerCase();
      const existing = groups.get(key);
      if (existing) {
        if (trademark.morionCode && !existing.morionCodes.includes(trademark.morionCode)) {
          existing.morionCodes.push(trademark.morionCode);
        }
        existing.isRecommended = existing.isRecommended || trademark.isRecommended;
        existing.requiresRecipe = existing.requiresRecipe || trademark.requiresRecipe;
      } else {
        groups.set(key, {
          name: trademark.name,
          morionCodes: trademark.morionCode ? [trademark.morionCode] : [],
          isRecommended: trademark.isRecommended,
          requiresRecipe: trademark.requiresRecipe,
        });
      }
    }
    return [...groups.values()];
  }

  // INNs whose trademark list is expanded; collapsed by default.
  protected readonly expandedInns = signal<Set<string>>(new Set());

  protected isInnExpanded(code: string): boolean {
    return this.expandedInns().has(code);
  }

  protected toggleInn(code: string): void {
    const next = new Set(this.expandedInns());
    if (next.has(code)) {
      next.delete(code);
    } else {
      next.add(code);
    }
    this.expandedInns.set(next);
  }

  protected readonly generalRecommendations = computed(() =>
    this.protocol()
      .generalRecommendations.map(item => item.nameUa || item.nameEn || item.code)
      .filter(Boolean)
  );

  protected readonly textSections = computed<TextSection[]>(() => {
    const protocol = this.protocol();
    return [
      { titleKey: 'protocols.detail.section.preventions', items: protocol.preventions },
      { titleKey: 'protocols.detail.section.referenceInformation', items: protocol.referenceInformation },
      { titleKey: 'protocols.detail.section.questionnaires', items: protocol.questionnaires },
    ].filter(section => section.items.length > 0);
  });

  protected readonly unresolvedGroups = computed<UnresolvedGroup[]>(() => {
    const groups = new Map<string, string[]>();
    for (const code of this.unresolved()) {
      const label = PROTOCOL_DICTIONARY_LABEL[code.dictionary] ?? '';
      const bucket = groups.get(label) ?? [];
      bucket.push(code.code);
      groups.set(label, bucket);
    }
    return [...groups.entries()].map(([label, codes]) => ({ label, codes }));
  });
}
